import { Injectable } from '@angular/core';

/**
 * Interface for storing pagination state
 */
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  sortField: string;
  sortDirection: string;
  totalRecords: number;
  selectedCasingId?: string;
}
/**
 * Service for managing and persisting pagination state across features.
 * Supports multiple storage keys for different components (casing-search, workorders, etc.)
 *
 * Storage is always localStorage for backward compatibility.
 */
@Injectable({
  providedIn: 'root'
})
export class PaginationStateService {
  private readonly DEFAULT_STORAGE_KEY = 'casing-search-pagination-state';

  /**
   * Save the current pagination state to localStorage
   * @param state The pagination state to persist
   * @param storageKey Optional storage key (defaults to 'casing-search-pagination-state' for backward compatibility)
   */
  savePaginationState(state: PaginationState, storageKey?: string): void {
    const key = storageKey || this.DEFAULT_STORAGE_KEY;
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Failed to save pagination state (${key}):`, error);
    }
  }

  /**
   * Retrieve the saved pagination state from localStorage
   * @param storageKey Optional storage key (defaults to 'casing-search-pagination-state' for backward compatibility)
   * @returns The saved pagination state or null if none exists
   */
  getPaginationState(storageKey?: string): PaginationState | null {
    const key = storageKey || this.DEFAULT_STORAGE_KEY;
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error(`Failed to retrieve pagination state (${key}):`, error);
      return null;
    }
  }

  /**
   * Clear the saved pagination state from localStorage
   * @param storageKey Optional storage key (defaults to 'casing-search-pagination-state' for backward compatibility)
   */
  clearPaginationState(storageKey?: string): void {
    const key = storageKey || this.DEFAULT_STORAGE_KEY;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to clear pagination state (${key}):`, error);
    }
  }
}
